import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api/client'
import type { Folder, FolderListResponse } from '../types'

/** Shared with the neighbour prefetch; `abortable` is for the prefetch only (see useProgress). */
export const foldersQuery = (abortable = false) => queryOptions({
  queryKey: ['folders'],
  queryFn: async (ctx) => {
    const { data } = await api.get<FolderListResponse>('/api/v1/folders/?page_size=100', abortable ? { signal: ctx.signal } : undefined)
    return data
  },
  // 30 s, as ['decks'] (see useDecks): every local change invalidates it.
  staleTime: 30_000,
})

export function useFolders() {
  return useQuery(foldersQuery())
}

export function useCreateFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (title: string) => {
      const { data } = await api.post<Folder>('/api/v1/folders/', { title })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folders'] }),
  })
}

export function useUpdateFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, title }: { id: string; title: string }) => {
      const { data } = await api.patch<Folder>(`/api/v1/folders/${id}`, { title })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folders'] }),
  })
}

export function useDeleteFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/folders/${id}`)
      return id
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folders'] })
      qc.invalidateQueries({ queryKey: ['decks'] })
    },
  })
}
